import { useEffect, useRef, useState } from 'react'

import { ActionIcon, Menu, Tooltip } from '@mantine/core'
import { DatePicker } from '@mantine/dates'

import StyledMenu from 'src/components/Overlays/StyledMenu/StyledMenu'
import IconChevronDown from 'src/icons/IconChevronDown'
import {
  atFollowUpHour,
  earliestFollowUpDay,
  FOLLOW_UP_PRESETS,
  followUpDateFromNow,
} from 'src/lib/followUp'

interface FollowUpMenuProps {
  disabled: boolean
  onSelect: (dueDate: Date) => void
}

/** The chevron half of the split save: "Save & follow up" presets or a date. */
const FollowUpMenu = ({ disabled, onSelect }: FollowUpMenuProps) => {
  const [opened, setOpened] = useState(false)
  const [pickingDate, setPickingDate] = useState(false)
  const pickerRef = useRef<HTMLDivElement>(null)

  // The "Pick a date" item unmounts when the calendar replaces it, which
  // would drop keyboard focus to <body> and take Escape with it.
  useEffect(() => {
    if (pickingDate) pickerRef.current?.focus()
  }, [pickingDate])

  const select = (dueDate: Date) => {
    setOpened(false)
    onSelect(dueDate)
  }

  return (
    <StyledMenu
      width={220}
      position="top-end"
      middlewares={{ flip: false, shift: true }}
      opened={opened}
      onChange={(next: boolean) => {
        setOpened(next)
        if (next) setPickingDate(false)
      }}
    >
      <Menu.Target>
        <Tooltip label="Save & follow up" position="top" className="text-xs">
          <ActionIcon
            disabled={disabled}
            variant="transparent"
            size={28}
            radius={0}
            aria-label="Save & follow up"
            onMouseDown={(event) => event.preventDefault()}
            className="!w-5 !min-w-0 rounded-r-md border-l-white/30 bg-doubleNickel-brand-500 transition-colors hover:bg-doubleNickel-brand-700 focus-visible:-outline-offset-2 disabled:border-l-doubleNickel-gray-200 disabled:bg-doubleNickel-gray-100"
          >
            <IconChevronDown
              className={`h-3 w-3 fill-none ${
                disabled
                  ? 'stroke-doubleNickel-gray-400'
                  : 'stroke-doubleNickel-white'
              }`}
            />
          </ActionIcon>
        </Tooltip>
      </Menu.Target>
      <Menu.Dropdown>
        {pickingDate ? (
          <div
            ref={pickerRef}
            tabIndex={-1}
            className="p-1 outline-none"
            data-testid="follow-up-date-picker"
          >
            <DatePicker
              size="xs"
              minDate={earliestFollowUpDay()}
              onChange={(date) => {
                if (date) select(atFollowUpHour(date))
              }}
            />
          </div>
        ) : (
          <>
            <Menu.Label>Save & follow up</Menu.Label>
            {FOLLOW_UP_PRESETS.map((preset) => (
              <Menu.Item
                key={preset.days}
                className="text-xs"
                onClick={() => select(followUpDateFromNow(preset.days))}
              >
                {preset.label}
              </Menu.Item>
            ))}
            <Menu.Divider />
            <Menu.Item
              className="text-xs"
              closeMenuOnClick={false}
              onClick={() => setPickingDate(true)}
            >
              Pick a date...
            </Menu.Item>
          </>
        )}
      </Menu.Dropdown>
    </StyledMenu>
  )
}

export default FollowUpMenu
